import React from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Box,
  TextField,
  Autocomplete,
} from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SearchableSelect from './SearchableSelect';
import FormSectionHeader from './FormSectionHeader';
import SectionCard from './SectionCard';
import { sa } from '../superAdminUi';
import { COUNTRY_OPTIONS, SCHOOL_TYPE_OPTIONS, CONTACT_ROLE_OPTIONS } from '../../constants/groupSchoolFieldOptions';

// The single controlled body for every school add/edit surface. It owns no state:
// `values` drives it and every change routes through `onFieldChange(name, value)`, so
// the host (Formik page, useState modal or wizard slide-over) keeps ownership.
//
// variant 'full' shows the main-contact and address detail; 'compact' drops those for
// the wizard's per-school cards where the group already carries the contact context.
//
// customerTypeOptions may be the dynamic CustomerType records ({ Id, Name, DisplayName })
// or plain { value, label } pairs; both are normalised here. CustomerType itself is
// carried as the JSON-string the multi-selects emit, matching the rest of the estate.

// Inputs and labels are tokenised to the Super Admin design system (Inter type, hairline
// strokes, teal focus) so the form reads the same as the rest of the surface.
const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: sa.font.family, fontSize: '14px', color: sa.color.text },
  '& .MuiInputLabel-root': { fontFamily: sa.font.family, fontSize: '14px', color: sa.color.textGray },
  '& .MuiInputLabel-root.Mui-focused': { color: sa.color.teal },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.stroke },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
};

const normaliseCustomerTypeOptions = (options = []) =>
  options.map((opt) => {
    if (opt && typeof opt === 'object') {
      return {
        value: opt.value ?? opt.Name ?? '',
        label: opt.label ?? opt.DisplayName ?? opt.Name ?? String(opt.value ?? ''),
      };
    }
    return { value: opt, label: String(opt) };
  });

const parseCustomerType = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return [];
  }
};

// The three-state characteristic selects: Yes -> true, No -> false, Not set -> null.
function ThreeStateSelect({ label, value, disabled, onChange }) {
  return (
    <FormControl fullWidth sx={fieldSx}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        disabled={disabled}
        value={value === null || value === undefined ? '' : value}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      >
        <MenuItem value="">Not set</MenuItem>
        <MenuItem value={true}>Yes</MenuItem>
        <MenuItem value={false}>No</MenuItem>
      </Select>
    </FormControl>
  );
}

export default function SchoolFormFields({
  values,
  onFieldChange,
  errors = {},
  variant = 'full',
  customerTypeOptions = [],
  disabledFields = {},
}) {
  const isCompact = variant === 'compact';
  const customerOptions = normaliseCustomerTypeOptions(customerTypeOptions);
  const selectedCustomerTypes = parseCustomerType(values.CustomerType);
  const isDisabled = (name) => Boolean(disabledFields[name]);
  const set = (name) => (e) => onFieldChange(name, e.target.value);

  const labelFor = (value) => {
    const match = customerOptions.find((o) => o.value === value);
    return match ? match.label : value;
  };

  return (
    <SectionCard title="School details" icon={<SchoolOutlinedIcon fontSize="small" />}>
      <Grid container spacing={1.5}>
      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          label="School Name"
          value={values.Name || ''}
          onChange={set('Name')}
          disabled={isDisabled('Name')}
          required
          error={Boolean(errors.Name)}
          helperText={errors.Name || ''}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          label="School URN"
          value={values.Urn || ''}
          onChange={set('Urn')}
          disabled={isDisabled('Urn')}
          error={Boolean(errors.Urn)}
          helperText={errors.Urn || ''}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          type="number"
          label="Number of Pupils"
          value={values.NumberOfPupil || ''}
          onChange={set('NumberOfPupil')}
          disabled={isDisabled('NumberOfPupil')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          label="Postcode"
          value={values.Postcode || ''}
          onChange={set('Postcode')}
          disabled={isDisabled('Postcode')}
        />
      </Grid>

      {!isCompact && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            sx={fieldSx}
            label="Address"
            value={values.Address || ''}
            onChange={set('Address')}
            disabled={isDisabled('Address')}
          />
        </Grid>
      )}

      {!isCompact && (
        <>
          <Grid item xs={12}>
            <FormSectionHeader title="Main contact" />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              sx={fieldSx}
              label="First Name"
              value={values.FirstName || ''}
              onChange={set('FirstName')}
              disabled={isDisabled('FirstName')}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              sx={fieldSx}
              label="Last Name"
              value={values.LastName || ''}
              onChange={set('LastName')}
              disabled={isDisabled('LastName')}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              sx={fieldSx}
              label="Contact Number"
              value={values.ContactNumber || ''}
              onChange={set('ContactNumber')}
              disabled={isDisabled('ContactNumber')}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Autocomplete
              freeSolo
              disabled={isDisabled('Role')}
              options={CONTACT_ROLE_OPTIONS}
              value={values.Role || ''}
              onChange={(_e, newValue) => onFieldChange('Role', newValue || '')}
              onInputChange={(_e, newValue, reason) => {
                // Ignore the programmatic 'reset' so a controlled value change never wipes
                // the field; keep typed free text and clears.
                if (reason === 'input' || reason === 'clear') onFieldChange('Role', newValue || '');
              }}
              renderInput={(params) => <TextField {...params} sx={fieldSx} label="Role" fullWidth />}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <FormSectionHeader title="Classification & characteristics" />
      </Grid>

      <Grid item xs={12} lg={6}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="school-form-type-label">School Type</InputLabel>
          <Select
            labelId="school-form-type-label"
            label="School Type"
            value={values.SchoolType || ''}
            onChange={set('SchoolType')}
            disabled={isDisabled('SchoolType')}
          >
            {SCHOOL_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} lg={6}>
        <SearchableSelect
          label="Country"
          value={values.Country}
          onChange={(val) => onFieldChange('Country', val || '')}
          options={COUNTRY_OPTIONS}
          disabled={isDisabled('Country')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="school-form-customer-type-label">Customer Type</InputLabel>
          <Select
            labelId="school-form-customer-type-label"
            multiple
            value={selectedCustomerTypes}
            onChange={(e) => onFieldChange('CustomerType', JSON.stringify(e.target.value))}
            input={<OutlinedInput label="Customer Type" />}
            disabled={isDisabled('CustomerType')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={labelFor(value)} />
                ))}
              </Box>
            )}
          >
            {customerOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          type="number"
          label="Lowest Age"
          value={values.LowestAge || ''}
          onChange={set('LowestAge')}
          disabled={isDisabled('LowestAge')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          type="number"
          label="Highest Age"
          value={values.HighestAge || ''}
          onChange={set('HighestAge')}
          disabled={isDisabled('HighestAge')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          type="number"
          label="LA Code"
          value={values.LA_Code || ''}
          onChange={set('LA_Code')}
          disabled={isDisabled('LA_Code')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <ThreeStateSelect
          label="Boarding"
          value={values.IsBoarding}
          disabled={isDisabled('IsBoarding')}
          onChange={(val) => onFieldChange('IsBoarding', val)}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <ThreeStateSelect
          label="Post 16"
          value={values.IsPost16}
          disabled={isDisabled('IsPost16')}
          onChange={(val) => onFieldChange('IsPost16', val)}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <ThreeStateSelect
          label="Nursery"
          value={values.HasNursery}
          disabled={isDisabled('HasNursery')}
          onChange={(val) => onFieldChange('HasNursery', val)}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel id="school-form-gender-label">School Gender</InputLabel>
          <Select
            labelId="school-form-gender-label"
            label="School Gender"
            value={values.SchoolGender === null || values.SchoolGender === undefined ? '' : values.SchoolGender}
            onChange={set('SchoolGender')}
            disabled={isDisabled('SchoolGender')}
          >
            <MenuItem value="">Not Specified</MenuItem>
            <MenuItem value={1}>Male</MenuItem>
            <MenuItem value={2}>Female</MenuItem>
            <MenuItem value={3}>Mixed</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormSectionHeader
          title="UK Schools only"
          subtitle="Only relevant for UK state and independent schools."
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          label="Religious Character"
          value={values.ReligousCharacter || ''}
          onChange={set('ReligousCharacter')}
          disabled={isDisabled('ReligousCharacter')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          label="Diocese"
          value={values.Diocese || ''}
          onChange={set('Diocese')}
          disabled={isDisabled('Diocese')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          type="number"
          label="FSM Percent"
          value={values.FSMPercent || ''}
          onChange={set('FSMPercent')}
          disabled={isDisabled('FSMPercent')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          label="Group Code"
          value={values.GroupCode || ''}
          onChange={set('GroupCode')}
          disabled={isDisabled('GroupCode')}
        />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField
          fullWidth
          sx={fieldSx}
          label="Is Federated"
          value={values.IsFederated || ''}
          onChange={set('IsFederated')}
          disabled={isDisabled('IsFederated')}
        />
      </Grid>
      </Grid>
    </SectionCard>
  );
}
